import PropTypes from 'prop-types'
import { useCallback, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { signOutUser } from '../../../actions/authActions'
import { getSignUpPageProgram } from '../../../actions/programActions'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import colors from '../../../assets/styles/globals.scss'

const useDataForErrors = ({ errors, course }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { selectedFamilyMember } = useSelector(state => state.programRegistration)
  const user = useSelector(state => state.user.current_user)

  const logoutUser = useCallback(e => {
    e.preventDefault()
    localStorage.setItem('redirectUrl', location.pathname)
    dispatch(signOutUser())
  }, [dispatch])

  const navigateToClass = useCallback(e => {
    e.preventDefault()
    dispatch(getSignUpPageProgram(course.id))
    navigate(`/landing/${course.community.id}/class/${course.id}`)
  }, [dispatch, course.id, course.community.id, navigate])

  return useMemo(() => {
    if (!errors) {
      return null
    }
    if (errors.userNotFamilyAdmin) {
      return {
        titleText: t('programs.registration.errors.userNotFamilyAdmin.title'),
        body: (
          <>
            <p>{t('programs.registration.errors.userNotFamilyAdmin.body1')}</p>
            <p>
              <Trans
                i18nKey='programs.registration.errors.userNotFamilyAdmin.body2'
                components={{ supportEmail: <span style={{ color: colors.secondaryColor, textDecoration: 'underline' }} /> }}
              />
            </p>
          </>
        ),
        linkAttributes: {
          linkMessage: t('programs.registration.errors.logOut'),
          navigateTo: '/login',
          onClick: logoutUser,
        },
      }
    }
    if (errors.courseUnavailable) {
      return {
        titleText: t('programs.registration.errors.courseUnavailable.title'),
        body: (
          <p>{t('programs.registration.errors.courseUnavailable.body')}</p>
        ),
        linkAttributes: {
          linkMessage: t('programs.common.browseOtherPrograms'),
          navigateTo: `/landing/${user.communities[0].id}`,
        },
      }
    }
    if (errors.enrollmentsClosed) {
      return {
        titleText: t('programs.registration.errors.enrollmentsClosed.title'),
        body: (
          <p>{t('programs.registration.errors.enrollmentsClosed.body')}</p>
        ),
        linkAttributes: {
          linkMessage: t('programs.common.browseOtherPrograms'),
          navigateTo: `/landing/${user.communities[0].id}`,
        },
      }
    }
    if (errors.invitationTokenInvalid) {
      return {
        titleText: t('programs.registration.errors.invitationTokenInvalid.title'),
        body: (
          <p>{t('programs.registration.errors.invitationTokenInvalid.body')}</p>
        ),
        linkAttributes: {
          linkMessage: t('programs.common.browseOtherPrograms'),
          navigateTo: `/landing/${user.communities[0].id}`,
        },
      }
    }
    if (errors.invalidFamilyMember) {
      return {
        titleText: t('programs.registration.errors.invalidFamilyMember.title', {
          firstName: selectedFamilyMember?.firstName,
          lastName: selectedFamilyMember?.lastName,
        }),
        body: (
          <p>{t('programs.registration.errors.invalidFamilyMember.body')}</p>
        ),
        linkAttributes: {
          linkMessage: t('programs.registration.errors.backToPrograms'),
          navigateTo: `/landing/${user.communities[0].id}`,
        },
      }
    }
    if (errors.packageUnavailable) {
      return {
        titleText: t('programs.registration.errors.packageUnavailable.title'),
        body: (
          <p>{t('programs.registration.errors.packageUnavailable.body')}</p>
        ),
        linkAttributes: {
          linkMessage: t('programs.registration.errors.backToProgram'),
          navigateTo: `/landing/${course.community.id}/class/${course.id}`,
          onClick: navigateToClass,
        },
      }
    }
    if (errors.userAlreadyInACommunity) {
      return {
        titleText: t('programs.registration.errors.userAlreadyInACommunity.title'),
        body: (
          <>
            <p>
              <Trans
                i18nKey='programs.registration.errors.userAlreadyInACommunity.body1'
                components={{ b: <span style={{ fontWeight: '700' }} /> }}
                values={{ programCommunity: course.community?.name, currentCommunity: user.communities[0].name }}
              />
            </p>
            <p>
              <Trans
                i18nKey='programs.registration.errors.userAlreadyInACommunity.body2'
                components={{
                  b: <span style={{ fontWeight: '700' }} />,
                  supportEmail: <span style={{ color: colors.secondaryColor, textDecoration: 'underline' }} />,
                }}
                values={{ programCommunity: course.community?.name }}
              />
            </p>
          </>
        ),
        linkAttributes: {
          linkMessage: t('programs.registration.errors.backToMyCommunity'),
          navigateTo: `/landing/${user.communities[0].id}`,
          isExternal: true,
        },
      }
    }
    return {
      titleText: t('programs.registration.errors.generic.title'),
      body: (
        <p>{t('programs.registration.errors.generic.body')}</p>
      ),
      linkAttributes: {
        linkMessage: t('programs.registration.errors.backToProgram'),
        navigateTo: `/landing/${course.community.id}/class/${course.id}`,
        onClick: navigateToClass,
      },
    }
  }, [course, errors, logoutUser, navigateToClass, selectedFamilyMember, t, user])
}

useDataForErrors.propTypes = {
  errors: PropTypes.shape({
    invalidFamilyMember: PropTypes.arrayOf(PropTypes.string),
    userNotFamilyAdmin: PropTypes.arrayOf(PropTypes.string),
    courseUnavailable: PropTypes.arrayOf(PropTypes.string),
    enrollmentsClosed: PropTypes.arrayOf(PropTypes.string),
    packageUnavailable: PropTypes.arrayOf(PropTypes.string),
    userAlreadyInACommunity: PropTypes.arrayOf(PropTypes.string),
  }),
  course: PropTypes.object,
}

export default useDataForErrors
